/**
 * "Save this to Contacts."
 *
 * Winnie, 15 September: "a successful external lookup should offer to save the result as a new
 * Contact, so the registry grows from real usage instead of needing to be populated by hand."
 *
 * Offered, not automatic. A lookup is often exploratory — somebody checking an ABN off an
 * invoice they may not end up entering — and a registry that silently absorbs every ABN anybody
 * ever typed is a registry nobody trusts. It also only appears when the answer came from the
 * ABR (`source: 'abr'`); a result the registry itself supplied is already in there.
 *
 * The record it writes carries the GST status the ABR returned, which is the field that lets
 * the next lookup for this ABN be answered without a second call. See contactsService.
 */
import { useState } from 'react'
import { AbrResult } from '../services/buyerService'
import { contactsService, ContactType } from '../services/contactsService'
import { toast } from './Toast'
import './ContactSavePrompt.css'

interface Props {
  /** The ABN that was looked up, however it was typed. */
  abn: string
  /** The result of that lookup. Nothing renders unless it succeeded against the ABR. */
  result: AbrResult | null
  /** What this counterparty is in the flow the prompt sits in. */
  type: ContactType
  /** Anything the surrounding form already knows, so the record is not a bare name. */
  suggest?: { name?: string | null; email?: string | null; phone?: string | null; address?: string | null }
  className?: string
}

export default function ContactSavePrompt({ abn, result, type, suggest, className }: Props) {
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  if (!result || !result.ok || result.source !== 'abr') return null

  const name = (suggest?.name || '').trim() || result.legalName || ''
  if (!name) return null

  const save = async () => {
    setBusy(true)
    setFailed(null)
    try {
      const c = await contactsService.create({
        name,
        legal_name: result.legalName || null,
        abn,
        email: suggest?.email || null,
        phone: suggest?.phone || null,
        address: suggest?.address || null,
        contact_type: [type],
        status: 'Active',
        gst_registered: result.gstRegistered ?? null,
        abn_status: result.abnStatus || null,
      })
      setSaved(c.contact_number || 'Saved')
      toast.saved()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not save that contact.'
      setFailed(msg)
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  if (saved) {
    return (
      <div className={`contact-save-prompt saved ${className || ''}`}>
        ✓ Saved to Contacts as {saved}. The next lookup for this ABN will answer from the registry.
      </div>
    )
  }

  return (
    <div className={`contact-save-prompt ${className || ''}`}>
      <span>Not in Contacts yet.</span>
      <button type="button" className="contact-save-btn" onClick={save} disabled={busy}>
        {busy ? 'Saving…' : `Save as ${type}`}
      </button>
      {failed && <span className="contact-save-error">{failed}</span>}
    </div>
  )
}
